import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password");
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${user.name}`);
      if (user.role === "admin") nav("/admin");
      else nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-paper grid lg:grid-cols-2" data-testid="login-page">
      <div className="flex flex-col justify-center px-6 md:px-16 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors mb-10" data-testid="login-back-link">
          <ArrowLeft size={16} /> Back to Yuukke
        </Link>
        <div className="flex items-center mb-8">
          <img src="/yuukke-logo.png" alt="Yuukke" className="h-9 w-auto" />
        </div>
        <h1 className="font-serif text-5xl font-medium tracking-[-0.01em] leading-[1.05] text-brand-ink">
          Welcome <span className="italic text-gradient-signature">back</span>
        </h1>
        <p className="mt-3 text-brand-muted text-[15px] max-w-md">Sign in to your buyer or business account.</p>

        <form onSubmit={submit} noValidate className="mt-10 space-y-4 max-w-md" data-testid="login-form">
          <label className="block">
            <span className="text-[13px] font-medium text-brand-ink mb-1.5 block">Email</span>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className="w-full bg-brand-cream border border-brand-line rounded-lg pl-11 pr-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all"
                data-testid="login-email" />
            </div>
          </label>
          <label className="block">
            <span className="text-[13px] font-medium text-brand-ink mb-1.5 block">Password</span>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                className="w-full bg-brand-cream border border-brand-line rounded-lg pl-11 pr-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all"
                data-testid="login-password" />
            </div>
          </label>
          <button type="submit" disabled={busy}
            className="w-full bg-brand-wine text-white rounded-lg py-4 font-semibold hover:bg-brand-wineDark hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            data-testid="login-submit">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>

        <p className="mt-8 text-sm text-brand-muted">
          New to Yuukke? <Link to="/signup" className="text-brand-wine font-semibold hover:underline" data-testid="login-signup-link">Create an account</Link>
        </p>
      </div>

      <div className="hidden lg:flex bg-brand-wine text-brand-cream p-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="relative">
          <span className="eyebrow on-dark">Business Exchange</span>
          <h2 className="mt-4 font-serif text-5xl font-medium tracking-[-0.01em] leading-tight">
            Every connection <span className="italic">creates opportunity.</span>
          </h2>
          <p className="mt-6 text-brand-cream/85 leading-relaxed max-w-md">
            A curated network of women-led businesses and the buyers who source with intention.
          </p>
        </div>
      </div>
    </div>
  );
}
